<template>
    <div
        v-if="loaded"
        class="af-register-form"
    >
        <div class="afu-w-full afu-p-3 sm:afu-p-5 afu-bg-neutral--light afu-shadow-secondary afu-rounded-secondary afu-m-auto sm:afu-max-w-md afu-w-100 afu-box-border">
            <div
                v-if="!is_oauth"
                class="afu-text-center afu-mb-11"
            >
                <h2 class="af-text--9xl afu-text-black afu-font-bold md:afu-font-semibold">
                    {{ $t('register.welcome_aboard') }}
                </h2>

                <p class="afu-mb-0 afu-text-black">
                    {{ $t('register.info') }}
                </p>
            </div>

            <C1Alert
                v-if="!!response.message"
                :variant="response.variant"
            >
                <span v-html="response.message" />
            </C1Alert>
            <div 
                v-if="!is_oauth"
                class="afu-register-with-accounts"
            >
                <AuthGoogle />
                <AuthLinkedIn />
                <div class="afu-text-center afu-w-full afu-my-10">
                    <span class="strike-text afu-font-light">Or register with your email</span>
                </div>
            </div>
            <ValidationObserver
                ref="registerForm"
                tag="form"
                @submit.stop.prevent="onSubmit"
            >
                <div class="afu-grid afu-grid-cols-2 afu-gap-4">
                    <ValidationProvider
                        name="first name"
                        rules="required"
                        v-slot="{ errors }"
                    >
                        <C1FormGroup>
                            <C1FormInput
                                id="first_name"
                                :label="$t('label.first_name')"
                                label-for="first_name"
                                v-model="first_name"
                                :is-invalid="!!errors[0]"
                                :invalid-feedback="errors[0]"
                                name="first_name"
                                :placeholder="$t('label.first_name')"
                            />
                        </C1FormGroup>
                    </ValidationProvider>

                    <ValidationProvider
                        name="last name"
                        rules="required"
                        v-slot="{ errors }"
                    >
                        <C1FormGroup>
                            <C1FormInput
                                id="last_name"
                                :label="$t('label.last_name')"
                                label-for="last_name"
                                v-model="last_name"
                                :is-invalid="!!errors[0]"
                                :invalid-feedback="errors[0]"
                                name="last_name"
                                :placeholder="$t('label.last_name')"
                            />
                        </C1FormGroup>
                    </ValidationProvider>
                </div>

                <ValidationProvider
                    name="email address"
                    rules="required|email"
                    v-slot="{ errors }"
                >
                    <C1FormGroup>
                        <C1FormInput
                            id="email"
                            :label="$t('label.email')"
                            label-for="email"
                            v-model="email"
                            :is-invalid="!!errors[0]"
                            :invalid-feedback="errors[0]"
                            name="email"
                            type="email"
                            :placeholder="$t('label.email_address')"
                            :disabled="is_oauth"
                        />
                    </C1FormGroup>
                </ValidationProvider>

                <ValidationProvider
                    name="password"
                    rules="required|verify_password"
                    v-slot="{ errors }"
                >
                    <C1FormGroup>
                        <C1FormInput
                            id="password"
                            :label="$t('label.password')"
                            label-for="password"
                            v-model="password"
                            :is-invalid="!!errors[0]"
                            :invalid-feedback="errors[0]"
                            type="password"
                            :placeholder="$t('label.enter_your_value', {value: $t('label.placeholder_password')})"
                        />
                    </C1FormGroup>
                </ValidationProvider>

                <ValidationProvider
                    name="confirm password"
                    rules="required|passwordc:@password"
                    v-slot="{ errors }"
                >
                    <C1FormGroup>
                        <C1FormInput
                            id="cpassword"
                            :label="$t('label.confirm_password')"
                            label-for="cpassword"
                            v-model="cpassword"
                            :is-invalid="!!errors[0]"
                            :invalid-feedback="errors[0]"
                            type="password"
                            :placeholder="$t('label.enter_your_value_again', {value: $t('label.placeholder_password')})"
                        />
                    </C1FormGroup>
                </ValidationProvider>

                <AfTerms :terms="getTerms" />

                <C1Button
                    type="submit"
                    variant="primary"
                    class="afu-mt-5"
                    size="lg"
                    block
                    :is-busy="spinner.register"
                >
                    {{ (is_oauth)? $t('label.create') : $t('label.register') }}
                </C1Button>
            </ValidationObserver>
        </div>
        <div
            v-if="!is_oauth"
            class="af-login-link afu-text-center afu-mt-5 md:afu-mt-0"
        >
            <span class="af-label afu-font-light">
                {{ $t('label.already_have_account') }}
            </span>
            <a
                :href="getLocalePath('/user-login')"
                class="afu-text-primary hover:afu-text-primary afu-underline hover:afu-no-underline afu-font-light"
            >
                {{ $t('label.login') }}
            </a>
        </div>
    </div>
    <!-- New page load skeleton -->
    <div
        v-else
        class="af-login-form"
    >
        <div class="afu-w-full afu-p-3 sm:afu-p-5 afu-bg-neutral--light afu-shadow-secondary afu-rounded-secondary afu-m-auto sm:afu-max-w-md afu-w-100 afu-box-border">
            <div class="af-attribute-skeleton af-attribute-filter afu-mt-4 afu-mb-11 afu-pt-5 afu-pb-2 afu-px-4 afu-shadow-secondary afu-rounded-secondary afu-border-neutral-light afu-bg-neutral-light">
                <div class="afu-bg-gray-500 afu-w-full afu-animate-pulse afu-h-8 afu-mb-4 afu-rounded-2xl" />
            </div>
            <div class="af-attribute-skeleton af-attribute-filter afu-my-4 afu-pt-5 afu-pb-2 afu-px-4 afu-shadow-secondary afu-rounded-secondary afu-border-neutral-light afu-bg-neutral-light">
                <div class="afu-flex afu-gap-4">
                    <div
                        class="afu-flex-1"
                        v-for="n in 2"
                        :key="n"
                    >
                        <div class="afu-bg-gray-400 afu-w-40 afu-animate-pulse afu-h-4 afu-mb-4 afu-rounded-2xl" />
                        <div class="afu-bg-gray-400 afu-w-full afu-animate-pulse afu-h-8 afu-mb-4 afu-rounded-2xl" />
                    </div>
                </div>
                <div
                    v-for="n in 3"
                    :key="n"
                >
                    <div class="afu-bg-gray-400 afu-w-40 afu-animate-pulse afu-h-4 afu-mb-4 afu-rounded-2xl" />
                    <div class="afu-bg-gray-400 afu-w-full afu-animate-pulse afu-h-8 afu-mb-4 afu-rounded-2xl" />
                </div>
                <div class="afu-bg-gray-400 afu-w-full afu-animate-pulse afu-h-4 afu-mb-1 afu-rounded-2xl" />
                <div class="afu-bg-gray-400 afu-w-full afu-animate-pulse afu-h-4 afu-mb-4 afu-rounded-2xl" />
                <div class="afu-bg-gray-500 afu-w-full afu-animate-pulse afu-h-11 afu-mb-4 afu-rounded-2xl" />
            </div>
            <div class="af-attribute-skeleton af-attribute-filter afu-my-4 afu-pt-5 afu-pb-2 afu-px-4 afu-shadow-secondary afu-rounded-secondary afu-border-neutral-light afu-bg-neutral-light">
                <div class="afu-bg-gray-400 afu-w-full afu-animate-pulse afu-h-6 afu-mb-4 afu-rounded-2xl" />
            </div>
        </div>
    </div>
</template>

<script>
    import { mapState } from 'vuex';
    import AuthMixin from '@/components/Auth/AuthMixin';
    import { ValidationObserver, ValidationProvider } from 'vee-validate';
    import C1Button from '@/components/C1Components/C1Button';
    import C1FormInput from '@/components/C1Components/C1FormInput';
    import C1Alert from '@/components/C1Components/C1Alert';
    import C1FormGroup from '@/components/C1Components/C1FormGroup';
    import AfTerms from '@/components/Partials/AfTerms';
    import Cookie from '@/services/cookie';
    import AuthGoogle from '@/components/Auth/AuthGoogle';
    import AuthLinkedIn from '@/components/Auth/AuthLinkedIn';

    export default {
        name: 'RegisterForm',

        components: {
            C1FormGroup,
            ValidationObserver,
            ValidationProvider,
            C1Alert,
            C1Button,
            C1FormInput,
            AfTerms,
            AuthGoogle,
            AuthLinkedIn,
        },

        mixins: [AuthMixin],

        data() {
            return {
                first_name: '',
                last_name: '',
                email: '',
                password: '',
                cpassword: '',
                response: {
                    variant: '',
                    message: '',
                },
                spinner: {
                    register: false,
                },
                is_oauth: false,
            };
        },

        computed: {
            ...mapState({
                $_apiMessage: state => state.apiMessage,
            }),

            getTerms() {
                let terms = window?.afConfig?.terms_content;
                if (terms) {
                    terms = JSON.parse(terms);
                } else {
                    terms = [];
                }
                return terms;
            },
        },

        async created() {
            await this.$_redirectIfAuthenticated();
            await this.$recaptchaLoaded();

            const oauthInfoStr = Cookie.getOauthInfo();
            if (!oauthInfoStr) {
                return;
            }

            const oauthInfo = JSON.parse(oauthInfoStr);
            if (oauthInfo?.email) {
                this.is_oauth = true;
                this.first_name = oauthInfo?.first_name || '';
                this.last_name = oauthInfo?.last_name || '';
                this.email = oauthInfo?.email;
            }
        },

        methods: {
            async onSubmit() {
                const validate = await this.$refs.registerForm.validate();
                if (!validate) {
                    return;
                }

                const token = await this.$recaptcha('register_direct');
                if (!token) {
                    return;
                }

                const payload = {
                    email: this.email,
                    first_name: this.first_name,
                    last_name: this.last_name,
                    password: this.password,
                    token: token,
                };

                this.spinner.register = true;

                this.$_register(payload)
                    .then(() => {
                        let redirect = '/user-dashboard';

                        const urlParams = new URLSearchParams(
                            window.location.search,
                        );
                        const redirectParam = urlParams.get('redirect');
                        if (redirectParam) {
                            redirect = redirectParam;
                        }

                        window.location.href = redirect;
                    })
                    .catch(error => {
                        const errorCode =
                            error?.response?.data?.error || 'ServerErrorException';
                        this.response.variant = 'danger';

                        if (errorCode =='RecaptchaTokenInvalid'){
                            this.response.message = error?.response?.data?.message;
                        }
                        else {
                            this.response.message = this.$_apiMessage[errorCode];
                        }
                        
                        this.spinner.register = false;
                    });
            },
        },
    };
</script>
